import {useEffect, useState} from 'react'
import {Link, Navigate, useNavigate} from 'react-router-dom'
import {useQueryClient} from 'react-query'
import {useAuth} from '../modules/auth'
import {
  getLegacyAuthRoles,
  getModuleAuthRoles,
  removeModuleAuthRoles,
  setModuleAuthRoles,
} from '../modules/auth/core/AuthHelpers'
import {useModule} from '../platform'

const ASSESSMENT_MODULE_CODE = 'assessment'
const MODULE_DASHBOARD_ROUTE = '/module-dashboard'

export const ASSESSMENT_PREVIEW_ENABLED =
  process.env.NODE_ENV === 'development' &&
  process.env.REACT_APP_ALLOW_LEGACY_MODULE_PREVIEW === 'true'

/**
 * Development-only bridge for reviewing the assessment module with an
 * existing legacy session. The legacy credentials remain in localStorage;
 * an isolated copy is written to the assessment module's sessionStorage slot.
 */
export const AssessmentPreviewEntry = () => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const {auth, currentUser, saveAuth} = useAuth()
  const {activateModule, defaultRoute, moduleCode} = useModule()
  const [error, setError] = useState<string>()

  useEffect(() => {
    if (!ASSESSMENT_PREVIEW_ENABLED || !currentUser || moduleCode) {
      return
    }

    const legacyAuthRoles = getLegacyAuthRoles()
    if (!legacyAuthRoles?.roles.length) {
      setError('Aktif legacy oturumu bulunamadı. Genel sisteme yeniden giriş yapıp tekrar deneyin.')
      return
    }

    const activeRoleIndex = Math.max(
      0,
      Math.min(legacyAuthRoles.activeRoleIndex || 0, Math.max(legacyAuthRoles.roles.length - 1, 0))
    )
    const legacyActiveAuth = legacyAuthRoles.roles[activeRoleIndex]

    if (!legacyActiveAuth?.token || legacyActiveAuth.token !== auth?.token) {
      setError(
        'Aktif legacy oturumu doğrulanamadı. Genel sisteme yeniden giriş yapıp tekrar deneyin.'
      )
      return
    }

    setModuleAuthRoles(ASSESSMENT_MODULE_CODE, {
      roles: legacyAuthRoles.roles,
      activeRoleIndex,
      defaultRoute: MODULE_DASHBOARD_ROUTE,
    })

    const previewAuthRoles = getModuleAuthRoles(ASSESSMENT_MODULE_CODE)
    const previewActiveAuth = previewAuthRoles?.roles[previewAuthRoles.activeRoleIndex]

    if (!previewAuthRoles || previewActiveAuth?.token !== legacyActiveAuth.token) {
      removeModuleAuthRoles(ASSESSMENT_MODULE_CODE)
      setError(
        'Ölçme ve Değerlendirme önizleme oturumu hazırlanamadı. Tarayıcı depolama izinlerini kontrol edin.'
      )
      return
    }

    if (activateModule(ASSESSMENT_MODULE_CODE) !== ASSESSMENT_MODULE_CODE) {
      removeModuleAuthRoles(ASSESSMENT_MODULE_CODE)
      setError('Ölçme ve Değerlendirme modül bağlamı etkinleştirilemedi.')
      return
    }

    saveAuth(previewActiveAuth, previewAuthRoles.activeRoleIndex)
    queryClient.clear()
    navigate(MODULE_DASHBOARD_ROUTE, {replace: true})
  }, [activateModule, auth?.token, currentUser, moduleCode, navigate, queryClient, saveAuth])

  if (!ASSESSMENT_PREVIEW_ENABLED) {
    return <Navigate to={currentUser ? defaultRoute : '/auth/login'} replace />
  }

  if (!currentUser) {
    return <Navigate to='/auth/login?module=assessment' replace />
  }

  if (moduleCode === ASSESSMENT_MODULE_CODE) {
    return <Navigate to={MODULE_DASHBOARD_ROUTE} replace />
  }

  if (moduleCode) {
    return <Navigate to={defaultRoute} replace />
  }

  if (error) {
    return (
      <main className='d-flex min-vh-100 align-items-center justify-content-center p-6'>
        <section className='card mw-600px w-100 p-8 text-center' role='alert'>
          <h1 className='fs-2 mb-4'>Ölçme ve Değerlendirme önizlemesi başlatılamadı</h1>
          <p className='text-muted mb-6'>{error}</p>
          <Link to='/dashboard' className='btn btn-primary align-self-center'>
            Genel dashboard'a dön
          </Link>
        </section>
      </main>
    )
  }

  return (
    <main
      className='d-flex min-vh-100 align-items-center justify-content-center p-6'
      aria-live='polite'
    >
      <div className='d-flex align-items-center gap-3'>
        <span className='spinner-border text-primary' aria-hidden='true'></span>
        <span>Ölçme ve Değerlendirme modül önizlemesi hazırlanıyor…</span>
      </div>
    </main>
  )
}
